import React, { useMemo, useState } from 'react';
import { Col, DatePicker, Form, Input, Row, Select, Typography } from 'antd';
import type { FormInstance } from 'antd';
import {
  Pestania,
  buscarClientes,
  buscarFarmacias,
  buscarOperaciones,
  leerTitularesFarmacia,
} from '../../api/oportunidadesApi';
import type { Catalogos } from '../../api/oportunidadesApi';
import { CamposOrigen } from './CamposOrigen';
import { SelectorRemoto } from './SelectorRemoto';
import type { OpcionRemota } from './SelectorRemoto';
import { MAXIMO_OBSERVACIONES_VENTA, MINIMO_OBSERVACIONES, etiquetaOperacion, observacionesValidas, servicioAplica } from './reglas';

const { Text } = Typography;

// Formularios de alta y edición (R-06..R-09, R-13, R-19). Las reglas se señalan en el campo; el
// backend las vuelve a comprobar y es el que manda.

export const reglaObservaciones = {
  validator: async (_: unknown, valor?: string) => {
    if (!observacionesValidas(valor)) throw new Error(`Las observaciones son obligatorias (mínimo ${MINIMO_OBSERVACIONES} caracteres).`);
  },
};

export const buscarFarmaciasComoOpciones = async (texto: string): Promise<OpcionRemota[]> =>
  (await buscarFarmacias(texto)).map((f) => ({
    value: f.idFarmacia,
    label: f.descripcion || `Farmacia ${f.idFarmacia}`,
    detalle: [f.titulares, f.baja ? 'de baja' : undefined].filter(Boolean).join(' · ') || undefined,
  }));

export const buscarClientesComoOpciones = async (texto: string): Promise<OpcionRemota[]> =>
  (await buscarClientes(texto)).map((c) => ({ value: c.idCliente, label: c.nombre ?? `Cliente ${c.idCliente}`, detalle: c.codigo }));

export const buscarOperacionesComoOpciones = async (texto: string): Promise<OpcionRemota[]> =>
  (await buscarOperaciones(texto)).map((o) => ({
    value: o.idOperacion,
    label: etiquetaOperacion(o),
    detalle: o.fase || undefined,
  }));

interface FormularioVentaProps {
  form: FormInstance;
  catalogos: Catalogos;
  edicion?: boolean;
  /** Etiquetas de lo ya guardado (edición): farmacia, titulares, EPC. */
  farmaciaInicial?: OpcionRemota;
  titularesIniciales?: OpcionRemota[];
  epcIniciales?: { entidad?: OpcionRemota; persona?: OpcionRemota };
}

/**
 * Venta (R-06): primero la farmacia, opcional. Al elegirla se cargan sus titulares actuales, que
 * se pueden quitar o completar con otros clientes; sin farmacia no suele haber titulares.
 */
export const FormularioVenta: React.FC<FormularioVentaProps> = ({
  form, catalogos, edicion, farmaciaInicial, titularesIniciales = [], epcIniciales,
}) => {
  const [opcionesTitulares, setOpcionesTitulares] = useState<OpcionRemota[]>(titularesIniciales);
  const [cargandoTitulares, setCargandoTitulares] = useState(false);
  const farmaciasIniciales = useMemo(() => (farmaciaInicial ? [farmaciaInicial] : []), [farmaciaInicial]);
  const idTipoOperacion: number | undefined = Form.useWatch('idTipoOperacion', form);
  const conServicio = servicioAplica(idTipoOperacion, catalogos.idTipoOperacionGestion);

  const alElegirFarmacia = async (idFarmacia?: number) => {
    if (!idFarmacia) return;
    setCargandoTitulares(true);
    try {
      const titulares = await leerTitularesFarmacia(idFarmacia);
      const opciones = titulares.map((t) => ({ value: t.idCliente, label: t.nombre ?? `Cliente ${t.idCliente}`, detalle: t.codigo }));
      setOpcionesTitulares(opciones);
      form.setFieldsValue({ idsTitulares: opciones.map((o) => o.value) });
    } finally {
      setCargandoTitulares(false);
    }
  };

  return (
    <>
      <Form.Item name="idFarmacia" label="Farmacia" extra="Opcional. Si la dejas vacía, la oportunidad queda sin farmacia identificada.">
        <SelectorRemoto
          buscar={buscarFarmaciasComoOpciones}
          opcionesIniciales={farmaciasIniciales}
          placeholder="Busca por dirección, población, titular o código"
          onChange={(v) => alElegirFarmacia(v)}
          aria-label="Farmacia"
        />
      </Form.Item>
      <Form.Item
        name="idsTitulares"
        label="Titulares"
        extra={cargandoTitulares ? 'Cargando los titulares actuales…' : 'Opcional. Puedes quitar titulares o añadir otros clientes.'}
      >
        <SelectorRemoto
          multiple
          buscar={buscarClientesComoOpciones}
          opcionesIniciales={opcionesTitulares}
          placeholder="Busca clientes por nombre o código"
          aria-label="Titulares"
        />
      </Form.Item>
      {/* Disposición (spec rev. 3): operación + servicio; iniciativa + forma de contacto; origen + detalle o EPC. */}
      <Row gutter={12}>
        <Col xs={24} md={12}>
          <Form.Item name="idTipoOperacion" label="Tipo de operación" rules={[{ required: true, message: 'El tipo de operación es obligatorio.' }]}>
            <Select
              placeholder="Elige"
              options={catalogos.tiposOperacion.map((t) => ({ value: t.id, label: t.descripcion }))}
              onChange={(v: number) => {
                if (!servicioAplica(v, catalogos.idTipoOperacionGestion)) form.setFieldsValue({ idTipoServicio: undefined });
              }}
            />
          </Form.Item>
        </Col>
        {conServicio && (
          <Col xs={24} md={12}>
            <Form.Item name="idTipoServicio" label="Servicio">
              <Select allowClear placeholder="Opcional" options={catalogos.tiposServicio.map((t) => ({ value: t.id, label: t.descripcion }))} />
            </Form.Item>
          </Col>
        )}
      </Row>
      <CamposOrigen
        form={form}
        catalogos={catalogos}
        epcIniciales={epcIniciales}
        junto={(
          <Form.Item name="idFormaContacto" label="Forma de contacto" rules={[{ required: true, message: 'La forma de contacto es obligatoria.' }]}>
            <Select placeholder="Elige" options={catalogos.formasContacto.map((t) => ({ value: t.id, label: t.descripcion }))} />
          </Form.Item>
        )}
      />
      {edicion && (
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item name="fechaGeneracion" label="Fecha de generación" rules={[{ required: true, message: 'La fecha es obligatoria.' }]}>
              <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="fechaObjetivoCaptacion" label="Fecha objetivo de captación">
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
      )}
      <Form.Item name="observaciones" label="Observaciones" required rules={[reglaObservaciones]}>
        <Input.TextArea rows={4} maxLength={MAXIMO_OBSERVACIONES_VENTA} showCount placeholder="Interés o circunstancias del caso" />
      </Form.Item>
    </>
  );
};

interface FormularioCompraProps {
  form: FormInstance;
  catalogos: Catalogos;
  pestania: Pestania.Compra | Pestania.Solicitud;
  edicion?: boolean;
  clienteInicial?: OpcionRemota;
  operacionesIniciales?: OpcionRemota[];
  epcIniciales?: { entidad?: OpcionRemota; persona?: OpcionRemota };
}

/** Compra (R-07) y solicitud de información (R-08: al menos una operación en fase 1-4). */
export const FormularioCompra: React.FC<FormularioCompraProps> = ({
  form, catalogos, pestania, edicion, clienteInicial, operacionesIniciales = [], epcIniciales,
}) => {
  const clientesIniciales = useMemo(() => (clienteInicial ? [clienteInicial] : []), [clienteInicial]);
  return (
    <>
      <Form.Item name="idCliente" label="Cliente" rules={[{ required: true, message: 'El cliente es obligatorio.' }]}>
        <SelectorRemoto
          buscar={buscarClientesComoOpciones}
          opcionesIniciales={clientesIniciales}
          placeholder="Busca por nombre o código"
          aria-label="Cliente"
        />
      </Form.Item>
      {pestania === Pestania.Solicitud && (
        <Form.Item
          name="idsOperaciones"
          label="Operaciones por las que pregunta"
          extra={<Text type="secondary">Solo operaciones en fase 1 a 4.</Text>}
          rules={[{ required: true, type: 'array', min: 1, message: 'Indica al menos una operación.' }]}
        >
          <SelectorRemoto
            multiple
            buscar={buscarOperacionesComoOpciones}
            opcionesIniciales={operacionesIniciales}
            placeholder="Busca por referencia o farmacia"
            aria-label="Operaciones"
          />
        </Form.Item>
      )}
      <CamposOrigen form={form} catalogos={catalogos} epcIniciales={epcIniciales} />
      {edicion && (
        <Form.Item name="fechaSolicitud" label="Fecha de solicitud" rules={[{ required: true, message: 'La fecha es obligatoria.' }]}>
          <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
        </Form.Item>
      )}
      <Form.Item name="observaciones" label="Observaciones" required rules={[reglaObservaciones]}>
        <Input.TextArea rows={4} showCount maxLength={20000} placeholder="Interés o circunstancias del caso" />
      </Form.Item>
    </>
  );
};
